/**
 * Agregações do canal a partir dos vídeos coletados.
 *
 * Mesma separação do `seoMetrics`: função pura, fora da tela, para os números
 * que orientam decisão do cliente serem testáveis sem montar componente.
 *
 * Os limiares seguem o que o YouTube publica para criadores. Onde não há
 * número oficial, o comentário diz que é convenção — e um caso merece atenção
 * especial: **tags**. O YouTube afirma que elas têm papel mínimo na descoberta,
 * então elas aparecem aqui como informação, não como problema a corrigir.
 */

/** `views`, `likes` e `comments` são BIGINT e chegam como string do Postgres. */
const num = (valor) => {
  const n = Number(valor);
  return Number.isFinite(n) ? n : 0;
};

const listaTags = (valor) => {
  if (Array.isArray(valor)) return valor.filter(Boolean);
  if (typeof valor === 'string')
    return valor
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean);
  return [];
};

const pct = (parte, total) => (total > 0 ? Math.round((parte / total) * 100) : 0);
const media = (total, quantidade) => (quantidade > 0 ? total / quantidade : 0);

/** Tamanho dos rankings de melhores e piores. */
const TOPO = 5;

export const LIMIARES = {
  // O YouTube não fixa tamanho ideal de título. 60 é onde ele começa a ser
  // truncado na busca e nas sugestões.
  TITULO_MAX: 60,

  // Os primeiros ~157 caracteres da descrição aparecem no resultado da busca.
  // Abaixo disso a descrição raramente informa o suficiente.
  DESCRICAO_MIN: 157,

  // Sem número oficial. 2% de (curtidas + comentários) sobre visualizações é a
  // convenção de mercado para engajamento saudável — serve de referência, não
  // de meta.
  ENGAJAMENTO_BOM: 2
};

/**
 * Faixa da taxa de engajamento, no vocabulário de veredicto de
 * `utils/auditoria`.
 *
 * Estava escrita dentro de `SecaoEngajamento`. Saiu de lá quando a tela de
 * análise da execução passou a mostrar a mesma taxa: a aba e a tela ficam a um
 * clique uma da outra, e duas cópias da regra acabariam discordando sobre o
 * mesmo número. A seção continua traduzindo para o vocabulário dela.
 */
export const estadoDoEngajamento = (taxa) =>
  taxa >= LIMIARES.ENGAJAMENTO_BOM ? 'ok' : taxa >= 1 ? 'aviso' : 'crit';

/** Uma tag só entra com pelo menos dois vídeos. */
export const MIN_VIDEOS_POR_TAG = 2;

/**
 * Tags ordenadas pelo engajamento dos vídeos que as carregam.
 *
 * Não confundir com `tagsMaisUsadas`, que conta frequência: aqui a pergunta é
 * "sobre o que este canal engaja", não "sobre o que ele publica".
 *
 * A taxa é agregada — soma de interações sobre soma de visualizações — e não a
 * média das taxas de cada vídeo. Um vídeo com dez visualizações e uma curtida
 * marca 10%, e numa média simples ele arrastaria a tag inteira para cima.
 *
 * Tag de um vídeo só fica de fora porque a taxa dela seria a do vídeo, não da
 * tag; e vídeo sem visualização não entra, porque taxa exige denominador. Isso
 * pode esvaziar a lista num canal onde nenhuma tag se repete — a tela diz isso
 * em vez de mostrar barras que não significam nada.
 */
export const tagsPorEngajamento = (videos, { minimo = MIN_VIDEOS_POR_TAG, topo = 6 } = {}) => {
  const porTag = new Map();

  videos.forEach((video) => {
    const views = num(video.views);
    if (views <= 0) return;

    listaTags(video.tags).forEach((tag) => {
      const atual = porTag.get(tag) || { tag, videos: 0, views: 0, interacoes: 0 };
      atual.videos += 1;
      atual.views += views;
      atual.interacoes += num(video.likes) + num(video.comments);
      porTag.set(tag, atual);
    });
  });

  return [...porTag.values()]
    .filter((x) => x.videos >= minimo)
    .map((x) => ({
      tag: x.tag,
      videos: x.videos,
      taxa: Number(((x.interacoes / x.views) * 100).toFixed(2))
    }))
    .sort((a, b) => b.taxa - a.taxa)
    .slice(0, topo);
};

export const metricasDesempenho = (videos) => {
  const total = videos.length;
  const views = videos.map((v) => num(v.views));
  const totalViews = views.reduce((s, v) => s + v, 0);

  const ordenados = [...videos].sort((a, b) => num(b.views) - num(a.views));
  const mediaViews = media(totalViews, total);

  // Faixas fixas: `reduce` não garante ordem e o gráfico sairia embaralhado.
  const faixas = [
    { name: '0–1k', min: 0, max: 1000 },
    { name: '1k–10k', min: 1000, max: 10000 },
    { name: '10k–100k', min: 10000, max: 100000 },
    { name: '100k+', min: 100000, max: Infinity }
  ];

  return {
    total,
    totalViews,
    mediaViews: Math.round(mediaViews),
    // Metade dos vídeos costuma ficar abaixo da média porque poucos sucessos a
    // puxam para cima. Contar quantos ficam é mais informativo que a média só.
    abaixoDaMedia: videos.filter((v) => num(v.views) < mediaViews).length,
    melhores: ordenados
      .slice(0, TOPO)
      .map((v) => ({ titulo: v.title || '(no title)', views: num(v.views) })),
    // Exclui quem já apareceu no topo: com menos de 11 vídeos as duas listas
    // se sobrepõem, e o mesmo título apareceria como melhor e pior na tela.
    piores: ordenados
      .slice(TOPO)
      .slice(-TOPO)
      .reverse()
      .map((v) => ({ titulo: v.title || '(no title)', views: num(v.views) })),
    distribuicao: faixas.map((f) => ({
      name: f.name,
      value: views.filter((n) => n >= f.min && n < f.max).length
    }))
  };
};

export const metricasMetadados = (videos) => {
  const total = videos.length;

  const semDescricao = videos.filter((v) => !v.description || v.description.trim() === '').length;
  const descricaoCurta = videos.filter((v) => {
    const d = (v.description || '').trim();
    return d.length > 0 && d.length < LIMIARES.DESCRICAO_MIN;
  }).length;
  const tituloLongo = videos.filter((v) => (v.title || '').length > LIMIARES.TITULO_MAX).length;
  const semThumbnail = videos.filter((v) => !v.thumbnail_url).length;
  const semTags = videos.filter((v) => listaTags(v.tags).length === 0).length;

  // Tags mais usadas, mantidas como informação de repertório do canal.
  const contagem = {};
  videos.forEach((v) => {
    listaTags(v.tags).forEach((t) => {
      contagem[t] = (contagem[t] || 0) + 1;
    });
  });

  return {
    total,
    semDescricao,
    descricaoCurta,
    tituloLongo,
    semThumbnail,
    semTags,
    tagsMaisUsadas: Object.entries(contagem)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 10)
      .map(([tag, quantidade]) => ({ tag, quantidade })),
    cobertura: [
      { name: 'metrica.descricao', ok: total - semDescricao, falta: semDescricao },
      { name: 'metrica.thumbnail', ok: total - semThumbnail, falta: semThumbnail }
    ]
  };
};

export const metricasEngajamento = (videos) => {
  const total = videos.length;

  const totalViews = videos.reduce((s, v) => s + num(v.views), 0);
  const totalLikes = videos.reduce((s, v) => s + num(v.likes), 0);
  const totalComments = videos.reduce((s, v) => s + num(v.comments), 0);

  const taxa = (v) => {
    const views = num(v.views);
    return views > 0 ? ((num(v.likes) + num(v.comments)) / views) * 100 : 0;
  };

  const comEngajamentoBaixo = videos.filter(
    (v) => num(v.views) > 0 && taxa(v) < LIMIARES.ENGAJAMENTO_BOM
  ).length;

  const porTaxa = [...videos].filter((v) => num(v.views) > 0).sort((a, b) => taxa(b) - taxa(a));

  return {
    total,
    // As somas saem junto: eram só denominador das taxas, e a tela de análise
    // da execução mostra os totais em si.
    totalViews,
    totalLikes,
    totalComments,
    taxaGeral: media((totalLikes + totalComments) * 100, totalViews),
    taxaCurtidas: media(totalLikes * 100, totalViews),
    taxaComentarios: media(totalComments * 100, totalViews),
    comEngajamentoBaixo,
    percentualBaixo: pct(comEngajamentoBaixo, total),
    maisEngajados: porTaxa
      .slice(0, 5)
      .map((v) => ({ titulo: v.title || '(no title)', taxa: Number(taxa(v).toFixed(2)) })),
    // Visualizações contra curtidas: pontos fora da diagonal são vídeos que
    // performaram melhor ou pior em engajamento do que o alcance sugeriria.
    dispersao: videos
      .filter((v) => num(v.views) > 0)
      .map((v) => ({ views: num(v.views), likes: num(v.likes), titulo: v.title }))
  };
};

/* O dia sai como índice, não como nome.
 *
 * A lista de nomes em inglês que ficava aqui não tinha como acompanhar o idioma
 * — este módulo é função pura sobre um array de vídeos, sem acesso ao contexto.
 * Quem renderiza formata o índice com `Intl`, que já sabe dizer "quarta-feira"
 * e "miércoles" sem tabela nossa nenhuma. */

export const metricasPublicacao = (videos) => {
  const comData = videos.filter((v) => v.published_at);

  const porDia = Array.from({ length: 7 }, (_, diaSemana) => ({ diaSemana, value: 0, views: 0 }));
  const porMes = {};

  comData.forEach((v) => {
    const data = new Date(v.published_at);
    if (Number.isNaN(data.getTime())) return;

    const dia = porDia[data.getDay()];
    dia.value += 1;
    dia.views += num(v.views);

    const chave = `${data.getFullYear()}-${String(data.getMonth() + 1).padStart(2, '0')}`;
    const mes = porMes[chave] || { value: 0, views: 0, likes: 0 };
    mes.value += 1;
    mes.views += num(v.views);
    mes.likes += num(v.likes);
    porMes[chave] = mes;
  });

  // Média de views por dia da semana, e não o total: um dia com mais vídeos
  // acumularia mais views sem que isso signifique que o dia funciona melhor.
  const porDiaComMedia = porDia.map((d) => ({
    ...d,
    mediaViews: d.value > 0 ? Math.round(d.views / d.value) : 0
  }));

  return {
    comData: comData.length,
    semData: videos.length - comData.length,
    porDia: porDiaComMedia,
    /* `mediaViews` e `mediaLikes` são por vídeo publicado no mês, pelo mesmo
       motivo do dia da semana: o total premiaria o mês em que se publicou
       mais, o que diz onde há mais vídeos e não onde eles funcionam melhor.
       `value` continua sendo a contagem, que é o que o gráfico de uploads
       desenha. */
    porMes: Object.entries(porMes)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([mes, m]) => ({
        mes,
        value: m.value,
        mediaViews: Math.round(media(m.views, m.value)),
        mediaLikes: Math.round(media(m.likes, m.value))
      }))
  };
};

/**
 * Contagem de pontos de atenção por aba.
 *
 * Tags ficam de fora de propósito: o YouTube diz que têm papel mínimo na
 * descoberta, e marcá-las como problema mandaria o cliente gastar tempo onde
 * o retorno é baixo.
 */
export const problemasPorSecao = (videos) => {
  const meta = metricasMetadados(videos);
  const eng = metricasEngajamento(videos);
  const des = metricasDesempenho(videos);
  const pub = metricasPublicacao(videos);

  return {
    desempenho: des.total > 0 ? des.distribuicao[0].value : 0,
    metadados: meta.semDescricao + meta.descricaoCurta + meta.tituloLongo + meta.semThumbnail,
    engajamento: eng.comEngajamentoBaixo,
    publicacao: pub.semData
  };
};
